import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import React from 'react';

import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import useTranslate from 'hooks/useTranslate';
import type { NoteFormInterface } from 'types';
import { noteFormDefaultValues, noteFormValidationSchema } from 'utils/validations/notes';

interface NoteEditorProps {
    open: boolean;
    mode: 'create' | 'edit';
    noteId?: number;
    initialValues?: NoteFormInterface;
    loading?: boolean;
    onClose: () => void;
    onSubmit: (values: NoteFormInterface) => void | Promise<void>;
}

const CharacterHints: React.FC = () => {
    const { translate } = useTranslate();
    const { values } = useFormikContext<NoteFormInterface>();
    const titleLeft = 64 - (values.title?.length ?? 0);
    const contentLeft = 1024 - (values.content?.length ?? 0);

    if (!values.title || !values.content) {
        return null;
    }

    return (
        <Stack>
            {values.title ? (
                <Typography
                    variant="caption"
                    color={titleLeft < 0 ? 'error' : 'text.secondary'}
                    sx={{ fontWeight: 500 }}
                >
                    {translate('remainingTitleText')}: {titleLeft}
                </Typography>
            ) : null}
            {values.content ? (
                <Typography
                    variant="caption"
                    color={contentLeft < 0 ? 'error' : 'text.secondary'}
                    sx={{ fontWeight: 500 }}
                >
                    {translate('remainingText')}: {contentLeft}
                </Typography>
            ) : null}
        </Stack>
    );
};

const NoteEditor: React.FC<NoteEditorProps> = ({
    open,
    mode,
    noteId,
    initialValues,
    loading,
    onClose,
    onSubmit,
}) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const formKey = mode === 'edit' ? `edit-${noteId ?? 'unknown'}` : 'create';

    return (
        <Dialog open={open} maxWidth="sm" fullWidth fullScreen={isMobile}>
            <Formik
                key={formKey}
                enableReinitialize
                initialValues={initialValues ?? noteFormDefaultValues}
                validationSchema={noteFormValidationSchema}
                onSubmit={onSubmit}
            >
                <Form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        flex: 1,
                        overflow: 'hidden',
                    }}
                >
                    <DialogTitle>
                        {translate(mode === 'create' ? 'addNote' : 'editNote')}
                    </DialogTitle>

                    <DialogContent
                        dividers
                        sx={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflowY: 'auto',
                        }}
                    >
                        <FormField
                            required
                            size="small"
                            name="title"
                            label="noteTitle"
                            disabled={loading}
                            maxLength={64}
                        />
                        <FormField
                            required
                            size="small"
                            name="content"
                            label="addTextHere"
                            multiline
                            minRows={isMobile ? 8 : 10}
                            disabled={loading}
                            maxLength={1024}
                        />
                        <CharacterHints />
                    </DialogContent>

                    <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('cancel')}
                        </Button>
                        <SubmitButton loading={loading} sx={{ textTransform: 'none' }}>
                            saveNote
                        </SubmitButton>
                    </DialogActions>
                </Form>
            </Formik>
        </Dialog>
    );
};

export default NoteEditor;
